import { AppContextState, AppContextType } from "src/AppContextProvider/type";
import { SUBSCRIPTION_STATUS } from "src/constants/constants";
import { FEATURE_FLAGS_ENUM } from "src/constants/featureFlag";
import { Countries, EuCountries } from "src/billing/constants";
import { toBillingV2PlanTier } from "src/domains/billing/trial";

/** The billing v2 subscription of a v2 org; undefined for v1 orgs and while the version loads. */
const billingV2Subscription = (context: AppContextType | AppContextState) =>
  context.billingVersion === "v2"
    ? (context.billingV2Account?.subscription ?? null)
    : undefined;

/**
 * Pixi offers are new-customers-only, and being in trial does not establish that: a reactivation
 * can put a former subscriber back in trial (GRWBKK-1968).
 *
 * `hasEverSubscribed` is billing.backend's answer, from invoice history; absent before that flag
 * shipped. `activatedAt` is the fallback and is trusted only when PRESENT — Chargebee clears it
 * whenever the subscription is not active, so its absence proves nothing. Never read its value.
 */
export const hasSubscribedBefore = (
  context: AppContextType | AppContextState,
): boolean => {
  const v2Subscription = billingV2Subscription(context);
  if (v2Subscription !== undefined) {
    return v2Subscription?.hasEverSubscribed === true;
  }
  const subscription = context.subscription as
    | { hasEverSubscribed?: boolean; activatedAt?: unknown }
    | null
    | undefined;
  if (!subscription) {
    return false;
  }
  if (subscription.hasEverSubscribed === true) {
    return true;
  }
  return !!subscription.activatedAt;
};

/**
 * In trial, flag on, and never subscribed before. Gates every Pixi offer surface: the subscribe
 * banner, the screen empty states, the onboarding banner and the pairing steps.
 */
export const isPixiTrialist = (context: AppContextType | AppContextState) => {
  const isPixiEnabled = context.shouldShowFeature(
    FEATURE_FLAGS_ENUM.PIXI_TRIALIST,
  );
  return isTrialist(context) && isPixiEnabled && !hasSubscribedBefore(context);
};

export const isTrialist = (context: AppContextType | AppContextState) => {
  const v2Subscription = billingV2Subscription(context);
  if (v2Subscription !== undefined) {
    return v2Subscription?.status === SUBSCRIPTION_STATUS.IN_TRIAL;
  }
  return context.subscription?.status === SUBSCRIPTION_STATUS.IN_TRIAL;
};

/**
 * A trialist who is already on a Pro plan. Used by onboarding to show the
 * Pro-only variant. Plan ids are formatted `{group}-{period}-{currency}`
 * (e.g. "pro-year-usd"), so the tier is the part before the first hyphen.
 */
export const isProTrialist = (
  context: AppContextType | AppContextState,
): boolean => {
  const v2Subscription = billingV2Subscription(context);
  if (v2Subscription !== undefined) {
    return (
      isTrialist(context) &&
      v2Subscription !== null &&
      toBillingV2PlanTier(v2Subscription.plan.id) === "pro"
    );
  }
  const planId =
    (context.subscription as { planId?: string } | null)?.planId ?? "";
  return isTrialist(context) && planId.startsWith("pro");
};

type CountryConfig<T> = {
  eu: T;
  us: T;
  ca: T;
  gb: T;
  default: T;
};

// Check if the country is in the EU (excluding GB)
const isEUNonGBCountry = (countryCode: string) =>
  EuCountries.filter((c) => c !== "GB").includes(countryCode);

const getCountrySpecificConfg = <T extends unknown>(
  countryCode: string,
  config: CountryConfig<T>,
): T => {
  if (isEUNonGBCountry(countryCode)) {
    return config.eu;
  }

  switch (countryCode) {
    case Countries.US:
    case "US":
      return config.us;

    case Countries.CA:
    case "CA":
      return config.ca;

    case Countries.GB:
    case "GB":
      return config.gb;

    default:
      return config.default;
  }
};

type PriceInfo = {
  fullPrice: number;
  discountPrice: number;
  currency: string;
  currencySymbol: string;
};

/**
 * Gets the pricing information based on the country code
 *
 * @param countryCode - Two-letter country code (e.g., 'US', 'GB')
 * @returns PriceInfo object containing pricing details
 */
export const getSCOSPrice = (countryCode: string): PriceInfo => {
  return getCountrySpecificConfg(countryCode, {
    eu: {
      fullPrice: 179,
      discountPrice: 89.5,
      currency: "EUR",
      currencySymbol: "€",
    },
    us: {
      fullPrice: 199,
      discountPrice: 99.5,
      currency: "USD",
      currencySymbol: "$",
    },
    ca: {
      fullPrice: 279,
      discountPrice: 139.5,
      currency: "CAD",
      currencySymbol: "CA$",
    },
    gb: {
      fullPrice: 149,
      discountPrice: 74.5,
      currency: "GBP",
      currencySymbol: "£",
    },
    default: {
      fullPrice: 199,
      discountPrice: 99.5,
      currency: "USD",
      currencySymbol: "$",
    },
  });
};

/**
 * Resolves the billing currency for the Pixi device from the country code.
 *
 * @remarks
 * The Pixi price is sourced from the Chargebee catalogue (keyed by currency), so we only need the currency here — the
 * amount is no longer hardcoded. An unmapped country returns `undefined` so callers show a neutral placeholder rather
 * than a misleading fallback price.
 *
 * @param countryCode - Two-letter country code (e.g. 'US', 'GB').
 * @returns The supported currency for the country, or `undefined` when unmapped.
 */
export const getPixiCurrency = (
  countryCode: string,
): "USD" | "GBP" | "EUR" | "CAD" | undefined =>
  getCountrySpecificConfg(countryCode, {
    us: "USD",
    ca: "CAD",
    gb: "GBP",
    eu: "EUR",
    default: undefined,
  });

export const getScosCouponCode = (countryCode: string): string => {
  return getCountrySpecificConfg(countryCode, {
    eu: "TRIAL50",
    us: "TRIAL50",
    ca: "TRIAL50",
    gb: "TRIAL50",
    default: "TRIAL50",
  });
};

interface WistiaVideoOptions {
  videoId: string;
  volume?: number;
  autoPlay?: boolean;
  captions?: boolean;
}

export const playWistiaVideo = (options: WistiaVideoOptions) => {
  const { videoId, volume = 1, autoPlay = true, captions = true } = options;
  // Load Wistia script
  const script = document.createElement("script");
  script.src = "https://fast.wistia.com/assets/external/E-v1.js";
  script.async = true;
  document.body.appendChild(script);

  // Initialize Wistia video with specified volume
  const handleWistiaInit = () => {
    if ((window as any)._wq) {
      (window as any)._wq = (window as any)._wq || [];
      (window as any)._wq.push({
        id: videoId,
        onReady: (video: any) => {
          video.volume(volume);

          // Handle autoplay if specified
          if (autoPlay) {
            video.play();
          } else {
            // Explicitly pause the video when autoPlay is false
            video.pause();
          }

          // Turn off captions if specified
          if (!captions) {
            if (video.plugin && video.plugin.captions) {
              video.plugin.captions.hide();
            }
          }
        },
      });
    }
  };

  // Check if Wistia is already loaded
  if ((window as any).Wistia) {
    handleWistiaInit();
  } else {
    // If not loaded yet, set up event listener
    script.onload = handleWistiaInit;
  }

  return () => {
    // Clean up script when component unmounts
    if (script.parentNode) {
      document.body.removeChild(script);
    }
  };
};
